import type { StyleDeclarationValue } from 'aphrodite';
import { type FC, useMemo } from 'react';

import { EmptyEmailSignatureTemplateId, TemplateType } from '../../../extViewmodels';
import { useTemplatesByType } from '../../../queries/Templates/useTemplatesByType';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';

/** Sentinel value applying the user's account-default signature server-side. */
export const DEFAULT_SIGNATURE_VALUE = 'default';

export type SignatureSelectProps = {
	/** Current signature id: `'default'`, `'none'`, or a specific template id. */
	value: string;
	onChange: (signatureTemplateId: string) => void;
	disabled?: boolean;
	/** Optional override styles for the trigger (e.g. to fill a toolbar cell). */
	triggerStyles?: StyleDeclarationValue;
};

/**
 * Signature picker backed by react-query (`useTemplatesByType`) — a modern
 * replacement for the legacy MobX `SignatureToolbarItem` (which is bound to
 * `TemplatesViewModel`). Options: the account default, "No signature", and each
 * of the user's signature templates. The chosen id flows to `useSendEmail` as
 * `signatureTemplateId`.
 */
export const SignatureSelect: FC<SignatureSelectProps> = ({ value, onChange, disabled = false, triggerStyles }) => {
	const query = useTemplatesByType({ type: TemplateType.Signature });

	const signatures = useMemo(
		() =>
			(query.data?.pages.flatMap(p => p.values ?? []) ?? []).filter((t): t is typeof t & { id: string } =>
				Boolean(t?.id)
			),
		[query.data]
	);

	return (
		<Select value={value} onValueChange={onChange} disabled={disabled}>
			<SelectTrigger styles={triggerStyles}>
				<SelectValue placeholder='No Signature' />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value={DEFAULT_SIGNATURE_VALUE}>Default signature</SelectItem>
				<SelectItem value={EmptyEmailSignatureTemplateId}>No signature</SelectItem>
				{signatures.map(sig => (
					<SelectItem key={sig.id} value={sig.id}>
						{sig.name || 'Untitled signature'}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
};
